import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Lock, Hash, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import apiClient, { getErrorMessage } from "@/lib/apiClient";

export default function InviteAccept() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joinErr, setJoinErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get(`/chat/invites/${code}`);
        setInvite(data);
      } catch (e) {
        setErr(getErrorMessage(e, "Invite not found or expired"));
      } finally { setLoading(false); }
    })();
  }, [code]);

  const join = async () => {
    if (!localStorage.getItem("token")) {
      // Not signed in — stash the invite and route to signup
      sessionStorage.setItem("pending_invite", code);
      navigate("/signup");
      return;
    }
    setJoining(true);
    setJoinErr("");
    try {
      const { data } = await apiClient.post(`/chat/invites/${code}/join`);
      toast.success(data.already_member ? "You're already in this channel" : "You joined the channel");
      navigate("/community");
    } catch (e) {
      const msg = getErrorMessage(e, "Join failed");
      setJoinErr(msg);
      toast.error(msg);
    }
    finally { setJoining(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16">
      <div className="glass rounded-2xl w-full max-w-md p-8 text-center">
        {loading ? (
          <div className="py-10"><Loader2 className="w-8 h-8 animate-spin text-cyan-300 mx-auto" /></div>
        ) : err ? (
          <>
            <p className="text-pink-400 font-bold text-lg">{err}</p>
            <button onClick={() => navigate("/")} className="neon-outline-btn mt-6 px-5 py-2">Back to home</button>
          </>
        ) : (
          <>
            <div className="w-16 h-16 rounded-full bg-cyan-400/10 border border-cyan-400/40 flex items-center justify-center mx-auto mb-4">
              {invite.room.is_public ? <Hash className="w-7 h-7 text-cyan-300" /> : <Lock className="w-7 h-7 text-cyan-300" />}
            </div>
            <p className="text-xs tracking-[0.2em] text-slate-500 font-bold">YOU'RE INVITED TO JOIN</p>
            <h1 className="font-display text-3xl text-white font-black mt-2">{invite.room.name}</h1>
            {invite.room.description && <p className="text-slate-400 text-sm mt-2">{invite.room.description}</p>}
            {invite.room.car_club_car && (
              <p className="text-cyan-300 text-xs mt-3 px-3 py-2 rounded-lg inline-block" style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.25)" }}>
                🏁 Car Club channel for {invite.room.car_club_car} drivers
              </p>
            )}
            <p className="text-slate-500 text-xs mt-3 flex items-center justify-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> {invite.room.member_count} members
            </p>
            {joinErr && (
              <p className="text-pink-400 text-sm mt-4 px-3 py-2 rounded-lg" style={{ background: "rgba(255,46,151,0.08)", border: "1px solid rgba(255,46,151,0.3)" }} data-testid="join-error">
                {joinErr}
              </p>
            )}
            <button onClick={join} disabled={joining} className="neon-btn w-full py-3 mt-6 flex items-center justify-center gap-2" data-testid="accept-invite-btn">
              {joining ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Accept invite
            </button>
            <p className="text-slate-500 text-xs mt-3">Not signed in? You'll be prompted to sign in or create an account.</p>
          </>
        )}
      </div>
    </div>
  );
}
